/**
 * Copyright © 2018 Emu Analytics
 */

import { FileExportFormat, IDataset, IIngestMessage } from './api-interfaces';
import { BaseEndpoints } from './base.endpoints';
import { Client } from './client';
import { WebSocketSubscribeCallback, WebSocketUnsubscriber } from './web-socket-gateway';

export interface DatasetQuery {
  [key: string]: any;
}

type QueryOutputFormat = 'plain' | 'json' | 'csv' | 'geojson';

export interface DatasetQueryOptions {
  [param: string]: any;
  format?: QueryOutputFormat;
  explain?: boolean | string;
  abortController?: AbortController;
}

export class DatasetEndpoints extends BaseEndpoints<IDataset> {
  constructor(client: Client) {
    super(client, 'datasets');
  }

  query(
    datasetId: string | DatasetQuery,
    query: DatasetQuery = {},
    params: DatasetQueryOptions | QueryOutputFormat = {}
  ): Promise<any> {
    // Support Flo.w RDF-style query definition
    if (typeof datasetId === 'object') {
      params = { ...datasetId.params, format: datasetId.format || 'json' };
      query = datasetId;
      datasetId = query.datasetId;
      delete query.datasetId;
      delete query.format;
    }

    if (typeof params === 'string') {
      params = { format: params };
    }

    let abortController: AbortController | null = null;
    if (params.abortController) {
      abortController = params.abortController;
      delete params.abortController;
    }

    return this.client
      ._post(`/api/v1/datasets/${datasetId}/query`, query, {
        params: {
          ...params,
          format: params.format || 'json',
          explain: params.explain || false
        },
        signal: abortController?.signal
      })
      .catch(e => {
        if (e.message === 'Network Error') {
          throw new Error('Query timed out');
        } else {
          throw e;
        }
      });
  }

  ingest(ingestMessage: IIngestMessage, params: Record<string, any> = {}): Promise<any> {
    return this.client._post(`/api/v1/datasets/${ingestMessage.datasetId}/ingest`, ingestMessage, params);
  }

  getExportUrl(datasetId: string, format: FileExportFormat): string {
    const credentials = this.client.accessToken
      ? `accesstoken=${this.client.accessToken}`
      : `apikey=${this.client.apiKey}`;
    return `${this.client.baseUrl}/api/v1/datasets/${datasetId}/export?format=${format}&${credentials}`;
  }

  subscribe(datasetId: string, cb: WebSocketSubscribeCallback): WebSocketUnsubscriber {
    return this.client._subscribe(`dataset/${datasetId}`, cb);
  }
}
